<template>
    <div id="app">
        <div class="environment-label">{{ envIdentifyLabel }}</div>
        <PreloadList @loaded="onLoaded" />
        <audio ref="BGM" src="@/assets/Sound/bgm.mp3" loop></audio>
        <div class="sound" @click="toggleSound">
            <img
                v-if="isBGMPlaying"
                src="@/assets/sound_on.png"
                alt=""
                width="100%"
            />
            <img v-else src="@/assets/sound_off.png" alt="" width="100%" />
        </div>
        <router-view
            v-if="!isLoading && isLiffReady"
            :class="{ 'is-mobile-horizontal': isMobileHorizontal }"
            :isReady="openMusicPop === false && todayNewLogin === false"
        />
        <div class="horizontal-hint">
            <div class="text">請將螢幕轉為直式</div>
        </div>
        <transition name="fade" mode="out-in">
            <OpenMusicPop v-if="openMusicPop" @interact="onInteractive" />
        </transition>
        <transition name="fade" mode="out-in">
            <SignIn
                v-if="!openMusicPop && todayNewLogin"
                @interact="todayNewLogin = false"
            />
        </transition>
        <transition name="fade" mode="out-in">
            <Loading v-if="isLoading || isConfigLoading" />
        </transition>
    </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import liff from '@line/liff';
import { mapMutations, mapActions } from 'vuex';
import PreloadList from '@/components/PreloadList.vue';
import Loading from '@/components/Loading.vue';
import OpenMusicPop from '@/components/OpenMusicConfirmation.vue';
import SignIn from '@/components/SignIn.vue';
import Sdk from '@/sdk';

export default {
    components: {
        PreloadList,
        OpenMusicPop,
        SignIn,
        Loading,
    },
    data() {
        return {
            openMusicPop: false,
            disclaimerPop: false,
            isBGMPlaying: true,

            isLoading: true,
            isConfigLoading: false,
            isLiffReady: false,

            isMobileHorizontal: false,
            todayNewLogin: false,

            envIdentifyLabel: undefined,
        };
    },
    created() {
        this.initLiff();
        this.envIdentifyLabel = process.env.VUE_APP_ENVIRONMENT_LABEL;
    },
    mounted() {
        this.initMounted();
    },
    methods: {
        ...mapMutations({
            updateToken: 'updateToken',
            updateLoginDayCount: 'updateLoginDayCount',
            setDefaultName: 'setDefaultName',
            raiseLiffInitFail: 'raiseLiffInitFail',
        }),
        ...mapActions({
            fetchConfig: 'config/fetchConfig',
        }),
        measureHeight() {
            const documentHeight = () => {
                const doc = document.documentElement;
                const height = window.visualViewport.height * window.visualViewport.scale;
                doc.style.setProperty('--doc-height', `${height}px`);
            };
            window.addEventListener('resize', documentHeight);
            documentHeight();
        },
        initMounted() {
            this.measureHeight();
            setTimeout(() => {
                this.openMusicPop = true;
            }, 300);
            this.$bus.$on('updateToken', this.updateToken);
            this.$bus.$on('unauthorized', () => {
                alert('登入資訊過期，請重新登入');
                this.$router.replace({ name: 'Login' });
            });
        },
        async initLiff() {
            try {
                const liffId = process.env.VUE_APP_LIFF_ID;
                await liff.init({ liffId });
                liff.ready
                    .then(async () => {
                        // console.log(liff.getAccessToken());
                        if (liff.isLoggedIn()) {
                            if (!['Home', 'JoinEvent'].includes(this.$route.name)) {
                                this.$router.replace({ name: 'Home' });
                            }
                            // 建議等拿到name再登入，否則要注意註冊名稱預設輸入的問題
                            await liff.getProfile().then((LIFF_INFO) => {
                                this.setDefaultName(LIFF_INFO.displayName);
                            });
                            const RES = await Sdk.loginWithLine(
                                liff.getAccessToken(),
                            );
                            this.isConfigLoading = true;
                            this.fetchConfig().finally(() => {
                                this.isConfigLoading = false;
                            });
                            this.updateLoginDayCount(RES.loginDayCount);
                            this.todayNewLogin = RES.todayNewLogin;
                        }
                    })
                    .catch((e) => {
                        Sentry.captureException(`liff ready failed: ${e}`);
                        this.raiseLiffInitFail();
                    })
                    .finally(() => {
                        this.isLiffReady = true;
                    });
            } catch (e) {
                Sentry.captureException(`liff init failed: ${e}`);
                this.raiseLiffInitFail();
            } finally {
                this.isLiffReady = true;
            }
        },
        toggleSound() {
            if (this.isBGMPlaying) {
                this.isBGMPlaying = false;
                this.$refs.BGM.pause();
            } else {
                this.isBGMPlaying = true;
                this.$refs.BGM.play();
            }
        },
        onInteractive(open) {
            if (open) {
                this.$refs.BGM.play();
                this.openMusicPop = false;
            } else {
                this.isBGMPlaying = false;
                this.openMusicPop = false;
            }
        },
        onLoaded() {
            this.isLoading = false;
        },
    },
};
</script>

<style lang="scss">
:root {
    --doc-height: 100%;
}
html {
    background-color: #57a2e7;
}
#app {
    position: relative;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100vh;
    height: var(--doc-height);
    @media screen and (orientation: landscape) and (min-height: 480px) {
        max-width: min(480px, 0.56 * var(--doc-height));
    }
    margin: 0 auto;

    font-family: "Noto Sans TC", sans-serif, Helvetica, Arial;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #003a5d;
    display: flex;
    justify-content: center;
    .v-main {
        display: initial;
        margin: auto;
    }
    > .main-view {
        overflow: auto;
        &::-webkit-scrollbar {
            display: none;
        }
    }
}
.environment-label {
    position: absolute;
    left: 10px;
    top: 10px;
    color: #fff;
    opacity: 0.7;
    z-index: 99999;
}
html,
body {
    margin: 0;
    overflow: auto;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.sound {
    cursor: pointer;
    position: absolute;
    z-index: 9999;
    right: 10px;
    top: 10px;
    width: 25px;
    img {
        display: block;
    }
}

#app > .horizontal-hint {
    background-color: #57a2e7;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    height: 100%;
    width: 100%;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    .text {
        color: #c96536;
        font-size: 30px;
        font-weight: bold;
    }
    @media screen and (orientation: portrait) {
        display: none;
    }
    @media only screen and (orientation: landscape) and (min-height: 480px) {
        display: none;
    }
}

.lottie svg {
    display: block;
}

@media only screen and (orientation: landscape) {
    .is-mobile-horizontal {
        display: none !important;
    }
}
@media screen and (max-width: 320px) {
    html {
        font-size: 14px !important;
    }
}
</style>
